/* eslint-disable no-param-reassign, no-restricted-syntax, no-prototype-builtins, no-shadow, no-useless-escape */
// Old trick to clone arrays, i.e. `aps.call(array)`.
const aps = Array.prototype.slice;

const identityFunc = (v) => v;

// returns true if obj is a basic Array, []
// duck type push as optimization; most checks are not arrays and will not
// have push set. this way we avoid the implicit toString() on the
// constructor.
const ARR_REGEXP = /^\s*function Array()/;
const isArray = (obj) => obj != null && obj.push && ARR_REGEXP.test(obj.constructor);

// returns true if obj is a basic Object, {}
const OBJ_REGEXP = /^\s*function Object()/;
const isObject = (obj) =>
  obj != null && typeof obj === 'object' && OBJ_REGEXP.test(obj.constructor);

const REGEXP_REGEXP = /^\s*function RegExp()/;
const isRegExp = (obj) => obj != null && REGEXP_REGEXP.test(obj.constructor);

// returns true if obj is one of javascript's basic types
const BASIC_TYPE_REGEXP = /^string|number|boolean|function$/i;
const isBasicType = (obj) => obj != null && (BASIC_TYPE_REGEXP.test(typeof obj) || isRegExp(obj));

const getDeep = (obj, parts, create) => {
  if (typeof parts === 'string') {
    parts = parts.split('.');
  } else {
    parts = aps.call(parts);
  }

  let lastObj = obj;
  let lastP;
  while (obj != null && parts.length) {
    const p = parts.shift();
    if ((obj[p] === undefined || (!isObject(obj[p]) && !isArray(obj[p]))) && create) {
      if (p === 0) {
        // eslint-disable-next-line no-multi-assign
        obj = lastObj[lastP] = [];
        obj[p] = {};
      } else {
        obj[p] = {};
      }
    }
    lastObj = obj;
    lastP = p;

    // Only descend into properties that the object directly owns. getDeep({}, '__proto__')
    // See for security vulnerability reproduction.
    // https://github.com/wistia/player-modern/issues/1800
    if (obj.hasOwnProperty(p)) {
      obj = obj[p];
    } else {
      obj = undefined;
    }
  }

  return obj;
};

// eslint-disable-next-line consistent-return
const setDeep = (obj, parts, value) => {
  if (typeof parts === 'string') {
    parts = parts.split('.');
  } else {
    parts = aps.call(parts);
  }
  const prop = parts.pop();
  obj = getDeep(obj, parts, true);
  if (obj != null && (isObject(obj) || isArray(obj)) && prop != null) {
    if (value != null) {
      obj[prop] = value;
    } else {
      delete obj[prop];
    }
  } else {
    // nothing to set or delete
    return undefined;
  }
};

const unsetDeep = (obj, parts) => setDeep(obj, parts);

// When merging, delete key from obj1 if the value from obj2 is null or
// undefined.
const legacyShouldDeleteFromMerge = (obj, k, v) => v == null;

//
// This is the meat and potatoes for iterating over
// javascript object/array structures.
//
// Call fn(node, path, parent, key) for each node in an object.
//
// We include the parent + key so that the callback can modify the leaf
// value in-place. We pass the path to the root node (the keys from root to
// leaf) so that we can serialize objects.
const eachDeep = (obj, fn, path, parent, key, customHaltDescentFn) => {
  if (path == null) {
    path = [];
  }
  if (isBasicType(obj) || (customHaltDescentFn && customHaltDescentFn(obj))) {
    fn(obj, path, parent, key);
  } else if (isObject(obj) || isArray(obj)) {
    fn(obj, path, parent, key);
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const newPath = aps.call(path);
        newPath.push(key);
        eachDeep(obj[key], fn, newPath, obj, key);
      }
    }
  } else {
    fn(obj, path, parent, key);
  }
};

// call fn(node, path, parent, key) for each leaf node in an object
const eachLeaf = (obj, fn, customHaltDescentFn) => {
  eachDeep(
    obj,
    (obj, path, parent, key) => {
      if ((!isArray(obj) && !isObject(obj)) || (customHaltDescentFn && customHaltDescentFn(obj))) {
        fn(obj, path, parent, key);
      }
    },
    undefined,
    undefined,
    undefined,
    customHaltDescentFn,
  );
};

// eachLeaf normally descends into arrays. This function does not, which is
// useful when we want to treat Arrays as atomic objects.
const eachObjectLeaf = (obj, fn) => eachLeaf(obj, fn, isArray);

// returns true if obj1 is a subset of obj2
const isSubsetDeep = (obj1, obj2) => {
  if (obj1 === obj2) {
    return true;
  }
  if ((obj1 != null && obj2 == null) || (obj1 == null && obj2 != null)) {
    return false;
  }
  let result = true;
  eachLeaf(obj1, (obj1LeafVal, path) => {
    const obj2LeafVal = getDeep(obj2, path);
    if (obj1LeafVal !== obj2LeafVal) {
      result = false;
    }
  });
  return result;
};

// Recursively merge the leaf values of obj2 into obj1. obj1 is modified
// in-place. Accepts objects or arrays. Always returns obj1.
//
// By default, we do not transform any merged values.
//
// We also use a non-ideal legacy strategy when merging null/undefined
// values from obj2: they will _delete_ corresponding keys in obj1. The more
// correct solution seems like it would be to _never_ delete from obj1, or
// at least to only delete `undefined` values, but maybe not null. We _may_
// be able to just make this change, but let's do one thing at a time.
const mergeOne = (
  obj1,
  obj2,
  transformerFn = identityFunc,
  shouldDeleteFn = legacyShouldDeleteFromMerge,
) => {
  if (isArray(obj2)) {
    if (!isArray(obj1)) {
      obj1 = [];
    }
    for (let i = 0; i < obj2.length; i++) {
      const v = obj2[i];
      // note that setting an index of an array will naturally increase the
      // length of the array. Array#push() is not necessary.
      if (obj1[i] == null && v != null) {
        if (isArray(v)) {
          obj1[i] = [];
        } else if (isObject(v)) {
          obj1[i] = {};
        } else {
          // Do nothing
        }
      }
      const result = mergeOne(obj1[i], v, transformerFn);
      if (shouldDeleteFn(obj2, i, result)) {
        delete obj1[i];
      } else {
        obj1[i] = result;
      }
    }
    return transformerFn(obj1);
  }
  if (isObject(obj2)) {
    for (const k in obj2) {
      if (obj2.hasOwnProperty(k) && (obj1.hasOwnProperty(k) || obj1[k] == null)) {
        const v = obj2[k];
        if (isArray(v)) {
          if (!isArray(obj1[k])) {
            obj1[k] = [];
          }
          mergeOne(obj1[k], v, transformerFn);
          obj1[k] = transformerFn(obj1[k]);
        } else if (isObject(v)) {
          if (!isObject(obj1[k])) {
            obj1[k] = {};
          }
          mergeOne(obj1[k], v, transformerFn);
          obj1[k] = transformerFn(obj1[k]);
        } else if (obj1 == null) {
          obj1 = {};
          if (!shouldDeleteFn(obj2, k, v)) {
            obj1[k] = transformerFn(v);
          }
        } else if (shouldDeleteFn(obj2, k, v)) {
          delete obj1[k];
        } else {
          obj1[k] = transformerFn(v);
        }
      }
    }
    return transformerFn(obj1);
  }
  // obj2 is not an object that can be cloned. Since we may recurse down
  // to this level, we should just return obj2, since it will overwrite
  // obj1.
  return transformerFn(obj2);
};

const merge = (obj1, ...objs) => {
  if (objs.length === 0) {
    return obj1;
  }
  for (let i = 0; i < objs.length; i++) {
    mergeOne(obj1, objs[i]);
  }
  return obj1;
};

const clone = (obj, transformerFn) => {
  if (isArray(obj)) {
    return mergeOne([], obj, transformerFn);
  }
  return mergeOne({}, obj, transformerFn);
};

const assignOne = (obj1, obj2) => {
  for (const k in obj2) {
    if (obj2.hasOwnProperty(k)) {
      obj1[k] = obj2[k];
    }
  }
  return obj1;
};

const assign = (obj1, ...objs) => {
  for (let i = 0; i < objs.length; i++) {
    assignOne(obj1, objs[i]);
  }
  return obj1;
};

// Given an object or array, iterates over each leaf and casts the value.
const castDeep = (obj) => {
  eachLeaf(obj, (leafVal, path, parent, key) => {
    if (typeof leafVal === 'string') {
      // eslint-disable-next-line no-use-before-define
      parent[key] = cast(leafVal);
    }
  });
  return obj;
};

const cast = (maybeStr) => {
  if (maybeStr == null) {
    return maybeStr;
  }

  if (isObject(maybeStr) || isArray(maybeStr)) {
    return castDeep(maybeStr);
  }

  const str = `${maybeStr}`;
  if (/^\-?[1-9]\d*?$/.test(str)) {
    return parseInt(str, 10);
  }
  if (str === '0') {
    return 0;
  }
  if (/^\-?\d*\.\d+$/.test(str)) {
    return parseFloat(str);
  }
  if (/^true$/i.test(str)) {
    return true;
  }
  if (/^false$/i.test(str)) {
    return false;
  }
  return maybeStr;
};

const except = (hash, keys) => {
  const result = {};
  const keyHash = {};
  for (let i = 0; i < keys.length; i++) {
    keyHash[keys[i]] = true;
  }
  for (const k in hash) {
    if (!keyHash[k]) {
      result[k] = hash[k];
    }
  }
  return result;
};

// true if each object is a subset of the other
const equalsDeep = (obj1, obj2) => isSubsetDeep(obj1, obj2) && isSubsetDeep(obj2, obj1);

const pick = (obj, keys) => {
  const result = {};
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    result[key] = obj[key];
  }
  return result;
};

const isEmpty = (obj) => {
  if (obj == null) {
    return true;
  }
  if (isArray(obj) && !obj.length) {
    return true;
  }
  if (isObject(obj)) {
    if (Object.keys(obj).length) {
      return false;
    }
    return true;
  }
  return false;
};

/**
 * Filters out a key/value pair from an object
 * and returns a new filtered object
 *
 * @template T
 *
 * @param {string | number} key - The key to filter by to remove item from object
 * @param {Record<string | number, T>} object - The object to filter an item from
 * @returns {Record<string | number, T>} The object with the key/value pair removed
 */
const filterObject = (key, object) =>
  Object.entries(object)
    .filter(([objKey]) => objKey !== key)
    .reduce((acc, [objKey, value]) => ({ ...acc, [objKey]: value }), {});

export {
  assign,
  cast,
  clone,
  eachLeaf,
  eachObjectLeaf,
  equalsDeep,
  except,
  filterObject,
  getDeep,
  isArray,
  isEmpty,
  isObject,
  isSubsetDeep,
  merge,
  pick,
  setDeep,
  unsetDeep,
};
